import React, { useState, useRef, useEffect } from 'react';
import DropdownFramer from '../../../components/framerMotion/DropdownFramer';
import Image from '../../../components/images/Image';
import AddInputs from '../../../components/Add/AddInputs';
import { useDispatch, useSelector } from 'react-redux';
import { setSourceField, setDestinationField, addSource, removeSource, addDestination, removeDestination, resetManualState } from '../../../store/slices/addManualSlice';
import S3ImageUpload from '../../../components/S3uploads/S3ImageUpload';
import TextArea from '../../../components/inputfields/TextArea';
import useApi from '../../../services/useApi';
import CurrencyInputField from '../../../components/inputfields/InputFieldWithCurrency';
import useTranslation from '../../../components/CustomHooks/useTranslation';
import { adminContents } from '../../AdminManagement/components/AdminManagementContent';
import InputShimmer from '../../../components/inputfields/InputShimmer';
import config from '../../../config/config';
import InputLabel from '../../../components/inputfields/InputLabel';

interface CustomDropdownProps {
    required?: boolean;
    disabled?: boolean;
    isSource?: boolean;
    sourceId?: number;
    loading?: boolean;
    index?: number;
    setErrors?: (errors: any) => void;
    errors?: any;
    singleDestination?: boolean;
    selectedOption?: string;
    sourceRefs?: React.MutableRefObject<(HTMLDivElement | null)[]>;
    payDueBill?: boolean;
}

const PurchaseTypeDropdown: React.FC<CustomDropdownProps> = ({
    required = false,
    disabled = false,
    isSource = true,
    sourceId = 1,
    loading = false,
    index = 0,
    setErrors,
    errors,
    singleDestination = false,
    selectedOption,
    sourceRefs,
    payDueBill
}) => {
    const dispatch = useDispatch();
    const { getAPI } = useApi();
    const { translate, notEnglish } = useTranslation(adminContents);
    const allDestinations = useSelector((state: any) => state.addManual?.destinations || {});
    const allSources = useSelector((state: any) => state.addManual?.sources || []);
    const [isAccountSelected, setIsAccountSelected] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [creditStatus, setCreditStatus] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [accounts, setAccounts] = useState<Record<string, string>[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchedAccount, setSearchedAccount] = useState('');
    const [showShimmer, setShowShimmer] = useState(true);

    useEffect(() => {
        if (payDueBill && !isSource) {
            setShowShimmer(true);
            const timer = setTimeout(() => {
                setShowShimmer(false);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [payDueBill, isSource]);

    const totalSourceAmount = allSources.reduce((total: number, source: any) => {
        const amount = Number(source.purchased_amount) || 0;
        return total + amount;
    }, 0);

    const totalDestinationAmount = Object.values(allDestinations).reduce((total: number, destination: any) => {
        const amount = parseFloat(destination.purchased_amount) || 0;
        return total + amount;
    }, 0);

    const totalDestinationPendingAmount = Object.values(allDestinations).reduce((total: number, destination: any) => {
        const amount = parseFloat(destination.pending_amount) || 0;
        return total + amount;
    }, 0);


    const handleAccounts = async () => {
        let type = (isSource && selectedOption === 'Paid') ? 'mno-purchase-source' : 'mno-purchase-destination';
        let response = await getAPI(`manual-entries/accounts?type=${type}&page_number=1`);
        setAccounts(response?.data?.data || []);
    };

    useEffect(() => {
        setErrors?.({});
        handleAccounts();
        dispatch(resetManualState());
        setIsAccountSelected(false);
    }, [selectedOption]);

    const loadMoreOptions = async (searchTerm: string, dropdownClick?: string) => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            let nextPage = 0;
            if (dropdownClick === 'dropdownClick') {
                nextPage = 1;
            } else {
                nextPage = currentPage + 1;
            }
            setCurrentPage(nextPage);

            let response = await getAPI(`manual-entries/accounts?type=${isSource ? 'mno-purchase-source' : 'mno-purchase-destination'}&page_number=${nextPage}${searchTerm !== '' ? `&search=${searchTerm}` : ''}`);

            setAccounts((prevAccounts) => [...prevAccounts, ...(response?.data?.data || [])]);
            setIsLoading(false);

            return response;
        } catch (error) {
            console.error("Error fetching more options:", error);
        }
    };

    const dispatchField = (id: string, value: any, idx: number) => {
        if (isSource) {
            dispatch(setSourceField({ source_account_id: idx, id, value }));
        } else {
            dispatch(setDestinationField({ destination_account_id: idx, id, value }));
        }
    };

    const handleAddSource = () => {
        dispatch(isSource ? addSource() : addDestination());
    };
    const handleErrorValidation = (id: string, idx: number) => {
        setErrors?.((prev: any) => {
            const newErrors = { ...prev };
            if (isSource) {
                if (newErrors.purchase_details?.source_accounts) {
                    if (id === 'remove') {
                        newErrors.purchase_details.source_accounts.splice(idx, 1);
                    } else if (newErrors.purchase_details.source_accounts[idx]) {
                        delete newErrors.purchase_details.source_accounts[idx][id];
                        if (Object.keys(newErrors.purchase_details.source_accounts[idx]).length === 0) {
                            newErrors.purchase_details.source_accounts.splice(idx, 1);
                        }
                    }
                }
            } else {
                if (newErrors.purchase_details?.destination_accounts) {
                    if (id === 'remove') {
                        newErrors.purchase_details.destination_accounts.splice(idx, 1);
                    } else if (newErrors.purchase_details.destination_accounts[idx]) {
                        delete newErrors.purchase_details.destination_accounts[idx][id];
                        if (Object.keys(newErrors.purchase_details.destination_accounts[idx]).length === 0) {
                            newErrors.purchase_details.destination_accounts.splice(idx, 1);
                        }
                    }
                }
            }
            return newErrors;
        });
    }

    const handleRemoveSource = (id: string, idx: number) => {
        if (isSource && allSources.length > 1) {
            dispatch(removeSource(id));
        }
        if (!isSource && allDestinations.length > 1) {
            dispatch(removeDestination(id));
        }
        handleErrorValidation('remove', idx);
    };
    const inputs = {
        "": {
            "Account": {
                required: true,
                disabled: disabled || false,
                id: `${isSource ? 'source' : 'destination'}_account_name`,
                type: "dropDown",
                options: accounts?.map(option => option.name) || [],
                tabIndex: index * 10,
                bddtestId: `${isSource ? 'source' : 'destination'}_account_name`,
                dropdownTestId: `${isSource ? 'source' : 'destination'}_account_name_value`,
                onSearch: setSearchedAccount,
            },
            "Transaction Type": {
                required: true,
                disabled: true,
                id: "transaction_type",
                type: "input",
                value: isSource ? 'Credit' : 'Debit',
                tabIndex: index * 10 + 1,
                bddTestId: isSource ? 'source-transaction-type' : 'destination-transaction-type',
                maxLength: 60,
            },
            "Purchased Date": {
                required: true,
                disabled: disabled || false,
                id: "purchased_date",
                type: "date",
                tabIndex: index * 10 + 2,
                bddTestId: isSource ? 'source-purchased-date' : 'destination-purchased-date',
                disabledDate: true,
            }
        },
    }
    const toggleDropdown = () => {
        setIsOpen((prev) => {
            if (prev) {
                setCurrentPage(1);
            }
            return !prev;
        });
    };

    const handleInputChange = (id: string, value: string | File, idx: number) => {
        handleErrorValidation(id, idx);
        if ((isSource && id === 'source_account_name') || (!isSource && id === 'destination_account_name')) {

            // Find the account object that matches the selected account name
            const selectedAccount = accounts.find(account => account.name === value);

            if (selectedAccount) {
                // Dispatch the account ID to the source_id or destination_id field
                const idField = isSource ? 'source_account_id' : 'destination_account_id';
                dispatchField(idField, selectedAccount.id, idx);

                // Also dispatch the account name as usual
                dispatchField(id, value, idx);
                setIsAccountSelected(true)
            }
        } else {
            // For other fields, dispatch as usual
            dispatchField(id, value, idx);
        }
    };

    const renderFields = (idx: number, source: any) => {
        if (payDueBill && !isSource && showShimmer) {
            return (
                <div className='flex flex-col gap-4 mt-2'>
                    <div className="flex mx-3 mt-3 h-full w-full gap-8">
                        <div className="shimmer skeleton w-[30%] h-12 mb-2"></div>     
                        <div className="shimmer skeleton w-[30%] h-12 mb-2"></div>     
                        <div className="shimmer skeleton w-[30%] h-12 mb-2"></div>                    
                    </div>
                    <div className="flex mx-3 mt-3 h-full w-full gap-8">
                        <div className="shimmer skeleton w-[30%] h-12 mb-2"></div>     
                        <div className="shimmer skeleton w-[30%] h-12 mb-2"></div>     
                    </div>
                    <div className="flex mx-3 mt-3 h-full w-full gap-8">
                        <div className="shimmer skeleton w-[30%] h-12 mb-2"></div>     
                    </div>
                    <div className="flex mx-3 mt-3 h-full w-full gap-8">
                        <div className="shimmer skeleton w-[95%] h-12 mb-2"></div>     
                    </div>
                </div>
            );
        }
        return (
            <>
                <div className="flex mx-3 mt-3 h-full">
                    <AddInputs
                        inputs={inputs}
                        totalInputCount={isSource ? 3 : 2}
                        inputValues={{
                            transaction_type: isSource ? 'Credit' : 'Debit',
                            ...(isSource ? { source_account_name: source['source_account_name'] } : { destination_account_name: source['destination_account_name'] }),
                            purchased_date: source['purchased_date'],
                        }}
                        onInputChange={(id, value) => handleInputChange(id, value, idx)}
                        showOnlyFirstField={(source[`${isSource ? 'source' : 'destination'}_account_name`] !== '') ? false : true}
                        customWidth={true}
                        errors={(isSource ? errors?.purchase_details?.source_accounts?.[idx] : errors?.purchase_details?.destination_accounts?.[idx]) || ''}
                        loadMoreOptions={loadMoreOptions}
                        index={idx}
                        isSource={isSource}
                    />
                </div>
                {source[`${isSource ? 'source' : 'destination'}_account_name`] !== '' && (
                    <>
                        <div className='flex flex-wrap mx-3 mt-3 gap-14 items-center' dir={notEnglish ? 'rtl' : 'ltr'}>
                            <div className={`w-[30%] h-full `}>
                                <CurrencyInputField
                                    inputItem={{
                                        labelName: 'Purchased Amount',
                                        id: 'purchased_amount',
                                    }}
                                    value={(isSource ? allSources[idx]['purchased_amount'] : allDestinations[idx]['purchased_amount']) || ''}
                                    onChange={(e) => handleInputChange('purchased_amount', e.target.value, idx)}
                                    customWidth='w-full'
                                    tabIndex={index * 10 + 4}
                                    required={true}
                                    disabled={disabled}
                                    testId={`${isSource ? 'source' : 'destination'}-purchase-amount-${idx}`}
                                    errorMessage={isSource ? errors?.purchase_details?.source_accounts?.[idx]?.purchased_amount : errors?.purchase_details?.destination_accounts?.[idx]?.purchased_amount}
                                />
                            </div>
                            {(payDueBill && !isSource) && <div className='w-[30%] h-full'>
                                <CurrencyInputField
                                    inputItem={{
                                        labelName: 'Pending Amount',
                                        id: 'pending_amount',
                                    }}
                                    value={allDestinations[idx]['pending_amount'] || ''}
                                    onChange={(e) => handleInputChange('pending_amount', e.target.value, idx)}
                                    customWidth='w-full'
                                    tabIndex={index * 10 + 4}
                                    required={true}
                                    testId={`destination-pending-amount-${idx}`}
                                    errorMessage={errors?.purchase_details?.destination_accounts?.[idx]?.pending_amount}
                                />
                            </div>}
                        </div>
                        <div className='mx-3 h-full mt-[-20px]'>
                            {!payDueBill ? <S3ImageUpload
                                heading=''
                                label="Upload Document"
                                disabled={false}
                                upload_id={"receipt_url"}
                                UploadPath={"uploads/manual-entries/reciepts"}
                                handleUpload={(file, upload_id) => handleInputChange(file, upload_id, idx)}
                                uploadedFile={(isSource ? allSources[idx]['receipt_url'] : allDestinations[idx]['receipt_url']) || ''}
                                id={`${isSource ? 'source' : 'destination'}-${idx}`}
                                required={true}
                                errorMessage={(isSource ? errors?.purchase_details?.source_accounts?.[idx]?.receipt_url : errors?.purchase_details?.destination_accounts?.[idx]?.receipt_url) || ''}
                                setCustomError={setErrors}
                            /> :
                                <div className='mt-10'>
                                    <InputLabel labelName={"Uploaded document"} id={`${isSource ? 'source' : 'destination'}-${idx}`} required={true} />
                                    <div
                                        className="w-[30%] flex justify-between gap-1 overflow-hidden text-ellipsis py-[15px] px-[18px] bg-disable_fill  rounded-[8px] border-disable_stroke border text-body1"
                                    >
                                        <p>{allDestinations[idx]['receipt_url']?.split("/").pop()}</p>
                                        <Image src="file-eye" className="cursor-pointer" objectFit={'contain'} onClick={() => {
                                            const fileUrl = config?.cdnURL + 'public/' + allDestinations[idx]['receipt_url'];
                                            window.open(fileUrl, "_blank");
                                        }} />
                                    </div>
                                </div>
                            }
                        </div>
                        {((payDueBill && allDestinations[idx]['notes'] !== "") || !payDueBill) && <div className='mx-3 h-full' dir={notEnglish ? 'rtl' : 'ltr'}>
                            <TextArea
                                label="Notes"
                                required={false}
                                placeholderRequied={false}
                                text={(isSource ? allSources[idx]['notes'] : allDestinations[idx]['notes']) || ''}
                                setText={!payDueBill ? (value: any) => handleInputChange('notes', value, idx) : () => { }}
                                textError={''}
                                setTextError={() => { }}
                                bgColor={payDueBill ? "bg-disable_fill" : "bg-background_blue"}
                                maxLength={250}
                                disabled={payDueBill ? true : false}
                                id={`${isSource ? 'source-note' : 'destination-note'}-${idx}`}
                            />
                        </div>}
                        {(isSource ? allSources.length === 1 : allDestinations.length === 1) && <hr className="border-t border-[#E5E9EB] w-[98%] mt-4 mx-auto" />}
                    </>
                )}
            </>
        );
    };    
    return (
        <div className="w-full mb-4" ref={dropdownRef}>
            {selectedOption === 'Paid' && <div className={`rounded-md flex justify-between h-[76px] p-4 items-center bg-background_blue`} onClick={toggleDropdown}>
                <div className="">
                    <div className="text-lg font-semibold text-gray-800">
                        {isSource ? `Source` : `Destination`}
                    </div>
                </div>
                <div className="flex items-center">
                    <DropdownFramer isDropdownVisible={isOpen}>
                        <Image
                            src="dropdown-down-arrow"
                            className={`${isOpen ? "image-active-filter" : ""}`}
                        />
                    </DropdownFramer>
                </div>
            </div>}

            {(isOpen || selectedOption === 'Advance') && (
                <div className={`w-full relative pt-1 top-[-4px]  pb-4 h-fit ${selectedOption === 'Paid' ? '' : 'rounded-md'} bg-background_blue`}>
                    {selectedOption === 'Paid' && <hr className="border-t border-[#E5E9EB] w-[98%] mx-auto" />}
                    <div>
                        {isSource ? allSources?.map((source: any, index: number) => (
                            <div key={index} ref={sourceRefs && (el => sourceRefs.current[index] = el)}>
                                {loading ? <InputShimmer /> : renderFields(index, source)}
                                <div className={`flex ${(allSources.length - 1 !== index || allSources.length === 20) ? 'justify-end' : 'justify-between'} mt-3 mx-3`}>
                                    {(allSources.length - 1 === index && allSources.length < 20) && <div
                                        className='flex flex-wrap gap-1 cursor-pointer'
                                        onClick={handleAddSource}
                                    >
                                        <Image src='plus' className='w-5 h-5' />
                                        <h1 id='addSourceButton' className='text-p_primary text-sm cursor-pointer'>Add</h1>
                                    </div>}
                                    {allSources.length > 1 && <div className='cursor-pointer' onClick={() => handleRemoveSource(source.id, index)}>
                                        <Image src='delete_blue' className='w-5 h-5' />
                                    </div>}
                                </div>
                                {(allSources.length > 1) && <hr className={`border-t ${allSources.length - 1 === index ? 'border-[#E5E9EB]' : 'border-p_primary'} w-[98%] mt-4 mx-auto`} />}
                            </div>
                        )) : allDestinations.map((destination: any, index: number) => (
                            <div key={index}>
                                {renderFields(index, destination)}
                                {singleDestination && <div className={`flex ${allDestinations.length - 1 !== index ? 'justify-end' : 'justify-between'} mt-3 mx-3`}>
                                    {allDestinations.length - 1 === index && <div
                                        className='flex flex-wrap gap-1 cursor-pointer'
                                        onClick={handleAddSource}
                                    >
                                        <Image src='plus' className='w-5 h-5 cursor-pointer' />
                                        <h1 className='text-p_primary text-sm cursor-pointer'>Add</h1>
                                    </div>}
                                    {allDestinations.length > 1 && <div className='cursor-pointer' onClick={() => handleRemoveSource(destination.id, index)}>
                                        <Image src='delete_blue' className='w-5 h-5' />
                                    </div>}
                                </div>}
                                {(allDestinations.length > 1) && <hr className={`border-t ${allDestinations.length - 1 === index ? 'border-[#E5E9EB]' : 'border-p_primary'} w-[98%] mt-4 mx-auto`} />}
                            </div>
                        ))}
                        {(isAccountSelected || (payDueBill && !showShimmer)) && (
                            <div className="mx-3 mt-3" dir={notEnglish ? 'rtl' : 'ltr'}>
                                <h1 className='text-sm font-semibold py-2'>Total {(isSource && selectedOption === 'Paid') ? 'Source' : (!isSource && selectedOption === 'Paid') ? 'Destination' : ''} Amount</h1>
                                <div className='w-80 h-14 border border-P_light_active rounded-lg bg-white cursor-not-allowed'>
                                    <h1 className='font-medium mx-3 my-[4.5%]'>AFA {isSource ? totalSourceAmount : !payDueBill ? totalDestinationAmount : totalDestinationPendingAmount}</h1>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseTypeDropdown;